import axios from "axios";

export async function is_user_administrator(token: any): Promise<boolean> {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const query = `
    query IsUserAdministrator {
      isUserAdministrator
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      { query },
      config
    );

    if (response.status === 200 && response.data?.data) {
      const isAdmin = response.data.data.isUserAdministrator;
      console.log("IsUserAdministrator response:", isAdmin);
      return isAdmin;
    }

    return false;
  } catch (error) {
    console.error("Error checking user administrator status:", error);
    return false;
  }
}

export async function is_user_power_user(token: any): Promise<boolean> {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const query = `
    query IsUserPowerUser {
      isUserPowerUser
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      { query },
      config
    );

    if (response.status === 200 && response.data?.data) {
      const isPowerUser = response.data.data.isUserPowerUser;
      console.log("IsUserPowerUser response:", isPowerUser);
      return isPowerUser;
    }

    return false;
  } catch (error) {
    console.error("Error checking user power user status:", error);
    return false;
  }
}

export async function get_aws_accounts(token: any) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const query = `
    query GetAwsAccounts {
      awsAccounts {
        awsAccountId
        displayName
        roleToAssume
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      { query },
      config
    );

    if (response.status === 200 && response.data?.data?.awsAccounts) {
      console.log("GetAwsAccounts response:", response.data.data.awsAccounts);
      return response.data.data.awsAccounts;
    }

    return [];
  } catch (error) {
    console.error("Error fetching AWS accounts:", error);
    return [];
  }
}

export async function get_aws_regions(token: any) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const query = `
    query GetAwsRegions {
      awsRegions {
        regionId
        name
        description
        awsAccountId
        awsAccountDisplayName
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      { query },
      config
    );

    if (response.status === 200 && response.data?.data?.awsRegions) {
      console.log("GetAwsRegions response:", response.data.data.awsRegions);
      return response.data.data.awsRegions;
    }

    return [];
  } catch (error) {
    console.error("Error fetching AWS regions:", error);
    return [];
  }
}

export async function get_paths(token: any) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  // Use GetUserPaths query for landing page (returns only paths user has access to)
  // Note: HotChocolate removes "Get" prefix, so GetUserPaths() becomes "userPaths" in GraphQL
  const query = `
    query GetUserPaths {
      userPaths {
        pathId
        pathPrefix
        displayName
        bucketId
        bucketName
        jiraTicket
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      { query },
      config
    );

    if (response.status === 200 && response.data?.data?.userPaths) {
      console.log("GetUserPaths response:", response.data.data.userPaths);

      // Transform the response to match the expected format
      const permissions = response.data.data.userPaths.map((path: any) => ({
        pathId: path.pathId,
        bucket: path.bucketName,
        bucketId: path.bucketId,
        bucketName: path.bucketName,
        pathPrefix: path.pathPrefix,
        jiraTicket: path.jiraTicket,
        displayName: path.displayName,
        overwriteExistingFiles: false // Default value
      }));

      return permissions;
    }

    return [];
  } catch (error) {
    console.error("Error fetching user paths:", error);
    return [];
  }
}

export async function get_user_paths_and_files(token: any) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const query = `
    query GetUserPathsAndFiles {
      userPathsAndFiles {
        bucketId
        bucketName
        jiraTicket
        paths {
          pathId
          pathPrefix
          displayName
        }
        files {
          fileId
          fileKey
          fileDisplayName
        }
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(graphqlEndpoint, { query }, config);

    if (response.status === 200 && response.data?.data?.userPathsAndFiles) {
      return response.data.data.userPathsAndFiles as Array<{
        bucketId: string;
        bucketName: string;
        jiraTicket: string;
        paths: Array<{ pathId: string; pathPrefix: string; displayName: string | null }>;
        files: Array<{ fileId: string; fileKey: string | null; fileDisplayName: string | null }>;
      }>;
    }

    return [];
  } catch (error) {
    console.error("Error fetching user paths and files:", error);
    return [];
  }
}

export async function get_s3_signed_url(token: any, path_setting: any) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const mutation = `
    mutation GenerateSignedUrl($input: GenerateSignedUrlInput!) {
      generateSignedUrl(input: $input) {
        url
        success
        statusCode
        errorMessage
        includeUsernameMetadata
        metadataUsername
        fileLogId
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      {
        query: mutation,
        variables: {
          input: {
            bucket: path_setting.bucket,
            pathPrefix: path_setting.pathPrefix,
            filename: path_setting.filename,
            jiraTicket: path_setting.jiraTicket
          }
        }
      },
      config
    );

    if (response.status === 200 && response.data?.data?.generateSignedUrl) {
      const result = response.data.data.generateSignedUrl;
      console.log("GenerateSignedUrl response:", result);

      if (result.success) {
        return result;
      } else {
        console.error("Failed to generate signed URL:", result.errorMessage);
        throw new Error(result.errorMessage || "Failed to generate signed URL");
      }
    }

    throw new Error("Invalid response from server");
  } catch (error: any) {
    console.error("Error generating signed URL:", error);
    throw error;
  }
}

export async function upload_file_to_aws(signed_url: string, file_data: any, metadataUsername: string | null, includeUsernameMetadata: boolean = true) {
  try {
    const headers: any = {};

    // x-amz-meta-username is included in X-Amz-SignedHeaders on the presigned URL.
    // The value MUST match exactly what the backend signed — we use the backend-returned
    // metadataUsername rather than deriving it client-side to avoid any encoding/casing
    // mismatch that would cause SignatureDoesNotMatch.
    if (includeUsernameMetadata && metadataUsername) {
      headers['x-amz-meta-username'] = metadataUsername;
    }

    const res = await axios.put(signed_url, file_data, { headers });
    return res.status;
  } catch (err: any) {
    // Extract the S3 error code from the XML response body for easier diagnosis
    const s3Xml: string = err?.response?.data ?? '';
    const codeMatch = s3Xml.match(/<Code>([^<]+)<\/Code>/);
    const msgMatch  = s3Xml.match(/<Message>([^<]+)<\/Message>/);
    const s3Code    = codeMatch?.[1] ?? 'Unknown';
    const s3Msg     = msgMatch?.[1]  ?? 'Unknown';
    console.error(`[upload_file_to_aws] S3 error — Code: ${s3Code}, Message: ${s3Msg}`, err);
    return 403;
  }
}

export async function get_files_by_bucket(token: any, bucketId: string) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const query = `
    query GetFilesByBucket($bucketId: String!) {
      filesByBucket(bucketId: $bucketId) {
        fileId
        bucketId
        bucketName
        fileKey
        fileDisplayName
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      {
        query: query,
        variables: {
          bucketId: bucketId
        }
      },
      config
    );

    if (response.status === 200 && response.data?.data?.filesByBucket) {
      return response.data.data.filesByBucket;
    }

    return [];
  } catch (error: any) {
    console.error("Error fetching files by bucket:", error);
    return [];
  }
}

export async function download_file(token: any, fileId: string) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const query = `
    query DownloadFile($input: DownloadFileInput!) {
      downloadFile(input: $input) {
        url
        success
        statusCode
        errorMessage
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      {
        query: query,
        variables: {
          input: {
            fileId: fileId
          }
        }
      },
      config
    );

    if (response.status === 200 && response.data?.data?.downloadFile) {
      const result = response.data.data.downloadFile;

      if (result.success) {
        return result.url;
      } else {
        console.error("Failed to generate download URL:", result.errorMessage);
        throw new Error(result.errorMessage || "Failed to generate download URL");
      }
    }

    throw new Error("Invalid response from server");
  } catch (error: any) {
    console.error("Error downloading file:", error);
    throw error;
  }
}

export async function add_file(token: any, bucketId: string, fileKey: string, fileDisplayName: string) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const mutation = `
    mutation AddFile($input: AddFileInput!) {
      addFile(input: $input) {
        fileId
        bucketId
        bucketName
        fileKey
        fileDisplayName
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      {
        query: mutation,
        variables: {
          input: {
            bucketId: bucketId,
            fileKey: fileKey,
            fileDisplayName: fileDisplayName
          }
        }
      },
      config
    );

    if (response.status === 200 && response.data?.data?.addFile) {
      return response.data.data.addFile;
    }

    throw new Error(response.data?.errors?.[0]?.message || "Failed to add file");
  } catch (error: any) {
    console.error("Error adding file:", error);
    throw error;
  }
}

export async function update_file(token: any, fileId: string, fileKey: string, fileDisplayName: string) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const mutation = `
    mutation UpdateFile($fileId: String!, $fileKey: String, $fileDisplayName: String) {
      updateFile(fileId: $fileId, fileKey: $fileKey, fileDisplayName: $fileDisplayName) {
        fileId
        bucketId
        bucketName
        fileKey
        fileDisplayName
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      {
        query: mutation,
        variables: {
          fileId: fileId,
          fileKey: fileKey,
          fileDisplayName: fileDisplayName
        }
      },
      config
    );

    if (response.status === 200 && response.data?.data?.updateFile) {
      return response.data.data.updateFile;
    }

    throw new Error(response.data?.errors?.[0]?.message || "Failed to update file");
  } catch (error: any) {
    console.error("Error updating file:", error);
    throw error;
  }
}

export async function delete_file(token: any, fileId: string) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const mutation = `
    mutation DeleteFile($input: DeleteFileInput!) {
      deleteFile(input: $input)
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      {
        query: mutation,
        variables: {
          input: {
            fileId: fileId
          }
        }
      },
      config
    );

    if (response.status === 200 && response.data?.data?.deleteFile !== undefined) {
      return response.data.data.deleteFile;
    }

    throw new Error(response.data?.errors?.[0]?.message || "Failed to delete file");
  } catch (error: any) {
    console.error("Error deleting file:", error);
    throw error;
  }
}

export async function check_file_exists_in_s3(
  token: any,
  bucketName: string,
  fileKey: string,
  roleArn: string | null,
  region: string
) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const query = `
    query CheckFileExistsInS3($bucketName: String!, $fileKey: String!, $roleArn: String, $region: String!) {
      checkFileExistsInS3(bucketName: $bucketName, fileKey: $fileKey, roleArn: $roleArn, region: $region)
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      {
        query: query,
        variables: {
          bucketName: bucketName,
          fileKey: fileKey,
          roleArn: roleArn,
          region: region
        }
      },
      config
    );

    if (response.status === 200 && response.data?.data?.checkFileExistsInS3 !== undefined) {
      return response.data.data.checkFileExistsInS3;
    }

    return false;
  } catch (error: any) {
    console.error("Error checking file exists in S3:", error);
    return false;
  }
}

export async function check_bucket_exists_in_s3(
  token: any,
  bucketName: string,
  roleArn: string | null,
  region: string
) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const query = `
    query CheckBucketExistsInS3($bucketName: String!, $roleArn: String, $region: String!) {
      checkBucketExistsInS3(bucketName: $bucketName, roleArn: $roleArn, region: $region)
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      {
        query: query,
        variables: {
          bucketName: bucketName,
          roleArn: roleArn,
          region: region
        }
      },
      config
    );

    if (response.status === 200 && response.data?.data?.checkBucketExistsInS3 !== undefined) {
      return response.data.data.checkBucketExistsInS3;
    }

    return false;
  } catch (error: any) {
    console.error("Error checking bucket exists in S3:", error);
    return false;
  }
}

export async function get_buckets(token: any) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  // Use GetAllBuckets for admin bucket management page
  // Note: HotChocolate removes "Get" prefix, so GetAllBuckets() becomes "allBuckets" in GraphQL
  const query = `
    query GetAllBuckets {
      allBuckets {
        bucketId
        bucketName
        jiraTicket
        displayName
        description
        regionId
        awsAccountId
        roleToAssume
        includeUsernameMetadata
        accessType
        managers {
          email
        }
        paths {
          pathId
          pathPrefix
          displayName
          description
          users {
            email
          }
        }
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      { query },
      config
    );

    if (response.status === 200 && response.data?.data?.allBuckets) {
      console.log("GetAllBuckets response:", response.data.data.allBuckets);
      return response.data.data.allBuckets;
    }

    // Check for GraphQL errors (e.g., unauthorized access)
    if (response.data?.errors && response.data.errors.length > 0) {
      const errorMessage = response.data.errors[0].message;
      console.error("GetAllBuckets GraphQL error:", errorMessage);
      throw new Error(errorMessage);
    }

    return [];
  } catch (error: any) {
    console.error("Error fetching all buckets:", error);

    // Re-throw the error so calling code can handle it
    if (error.response?.data?.errors) {
      const errorMessage = error.response.data.errors[0]?.message || "Failed to fetch buckets";
      throw new Error(errorMessage);
    }

    throw error;
  }
}

export async function get_users_and_buckets(token: any) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const query = `
    query GetUsersAndBuckets {
      usersAndBuckets {
        buckets {
          bucketId
          bucket
          pathPrefix
          jiraTicket
          displayName
          overwriteExistingFiles
          region
          checked
        }
        userPaths {
          user
          permissions {
            bucket
            pathPrefix
            jiraTicket
            displayName
            overwriteExistingFiles
          }
          permissionsStringArray
        }
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      { query },
      config
    );

    if (response.status === 200 && response.data?.data?.usersAndBuckets) {
      console.log("GetUsersAndBuckets response:", response.data.data.usersAndBuckets);
      return response.data.data.usersAndBuckets;
    }

    // Check for GraphQL errors (e.g., unauthorized access)
    if (response.data?.errors && response.data.errors.length > 0) {
      const errorMessage = response.data.errors[0].message;
      console.error("GetUsersAndBuckets GraphQL error:", errorMessage);
      throw new Error(errorMessage);
    }

    return { buckets: [], userPaths: [] };
  } catch (error: any) {
    console.error("Error fetching users and buckets:", error);

    // Re-throw the error so calling code can handle it
    if (error.response?.data?.errors) {
      const errorMessage = error.response.data.errors[0]?.message || "Failed to fetch users and buckets";
      throw new Error(errorMessage);
    }

    throw error;
  }
}

// Delete a bucket record from the database (Admin only)
// NOTE: This ONLY deletes the database record, NOT the actual S3 bucket in AWS
export async function delete_bucket(token: any, bucketId: string) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const mutation = `
    mutation DeleteBucket($bucketId: String!) {
      deleteBucket(bucketId: $bucketId)
    }
  `;

  const variables = {
    bucketId: bucketId
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      {
        query: mutation,
        variables: variables,
      },
      {
        headers: {
          Authorization: `Bearer ${id_token}`,
        },
      }
    );

    if (response.status === 200 && response.data?.data?.deleteBucket === true) {
      console.log("Bucket deleted successfully:", bucketId);
      return true;
    }

    // Check for GraphQL errors
    if (response.data?.errors && response.data.errors.length > 0) {
      const errorMessage = response.data.errors[0].message;
      console.error("DeleteBucket GraphQL error:", errorMessage);
      throw new Error(errorMessage);
    }

    return false;
  } catch (error: any) {
    console.error("Error deleting bucket:", error);

    if (error.response?.data?.errors) {
      const errorMessage = error.response.data.errors[0]?.message || "Failed to delete bucket";
      throw new Error(errorMessage);
    }

    throw error;
  }
}

// Update file log after upload (User)
export async function update_file_log(token: any, fileLogId: string, status: string) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const mutation = `
    mutation UpdateFileLog($fileLogId: String!, $frontendStatus: String!) {
      updateFileLog(input: { fileLogId: $fileLogId, frontendStatus: $frontendStatus })
    }
  `;

  const variables = {
    fileLogId: fileLogId,
    frontendStatus: status
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      {
        query: mutation,
        variables: variables,
      },
      {
        headers: {
          Authorization: `Bearer ${id_token}`,
        },
      }
    );

    if (response.status === 200 && response.data?.data?.updateFileLog === true) {
      console.log("FileLog updated successfully:", fileLogId, "status:", status);
      return true;
    }

    // Check for GraphQL errors
    if (response.data?.errors && response.data.errors.length > 0) {
      const errorMessage = response.data.errors[0].message;
      console.error("UpdateFileLog GraphQL error:", errorMessage);
      throw new Error(errorMessage);
    }

    return false;
  } catch (error: any) {
    console.error("Error updating file log:", error);

    if (error.response?.data?.errors) {
      const errorMessage = error.response.data.errors[0]?.message || "Failed to update file log";
      throw new Error(errorMessage);
    }

    throw error;
  }
}

// Get all users with their permissions (admin only)
export async function get_users(token: any) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const query = `
    query GetUsers {
      users {
        user
        permissions {
          bucket
          pathPrefix
          jiraTicket
          displayName
          overwriteExistingFiles
        }
        permissionsStringArray
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      { query },
      config
    );

    if (response.status === 200 && response.data?.data?.users) {
      const users = response.data.data.users;
      console.log("GetUsers response:", users);
      return users;
    }

    // Check for GraphQL errors (e.g., unauthorized access)
    if (response.data?.errors && response.data.errors.length > 0) {
      const errorMessage = response.data.errors[0].message;
      console.error("GetUsers GraphQL error:", errorMessage);
      throw new Error(errorMessage);
    }

    return [];
  } catch (error: any) {
    console.error("Error fetching users:", error);

    // Re-throw the error so calling code can handle it
    if (error.response?.data?.errors) {
      const errorMessage = error.response.data.errors[0]?.message || "Failed to fetch users";
      throw new Error(errorMessage);
    }

    throw error;
  }
}

// Get file logs (Admin sees all, Power User sees logs for managed buckets)
export async function get_file_logs(token: any) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const query = `
    query GetFileLogs {
      fileLogs {
        fileLogId
        filename
        path
        bucket
        awsAccountId
        region
        jiraTicket
        status
        uploadedBy
        requestedAt
        uploadedAt
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      { query },
      config
    );

    if (response.status === 200 && response.data?.data?.fileLogs) {
      const fileLogs = response.data.data.fileLogs;
      console.log("GetFileLogs response:", fileLogs);
      return fileLogs;
    }

    // Check for GraphQL errors (e.g., unauthorized access)
    if (response.data?.errors && response.data.errors.length > 0) {
      const errorMessage = response.data.errors[0].message;
      console.error("GetFileLogs GraphQL error:", errorMessage);
      throw new Error(errorMessage);
    }

    return [];
  } catch (error: any) {
    console.error("Error fetching file logs:", error);

    // Re-throw the error so calling code can handle it
    if (error.response?.data?.errors) {
      const errorMessage = error.response.data.errors[0]?.message || "Failed to fetch file logs";
      throw new Error(errorMessage);
    }

    throw error;
  }
}

export async function create_user(token: any, email: string) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const mutation = `
    mutation CreateUser($input: CreateUserInput!) {
      createUser(input: $input) {
        success
        message
        errorMessage
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      {
        query: mutation,
        variables: {
          input: {
            email: email
          }
        }
      },
      config
    );

    if (response.status === 200 && response.data?.data?.createUser) {
      const result = response.data.data.createUser;
      if (result.success) {
        console.log("User created successfully:", result.message);
        return true;
      } else {
        console.error("Failed to create user:", result.errorMessage);
        throw new Error(result.errorMessage);
      }
    }

    if (response.data?.errors) {
      const errorMessage = response.data.errors[0].message;
      console.error("CreateUser GraphQL error:", errorMessage);
      throw new Error(errorMessage);
    }

    return false;
  } catch (error: any) {
    console.error("Error creating user:", error);
    throw error;
  }
}

export async function update_user(token: any, email: string, isActive?: boolean) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const mutation = `
    mutation UpdateUser($input: UpdateUserInput!) {
      updateUser(input: $input) {
        success
        message
        errorMessage
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      {
        query: mutation,
        variables: {
          input: {
            email: email,
            isActive: isActive
          }
        }
      },
      config
    );

    if (response.status === 200 && response.data?.data?.updateUser) {
      const result = response.data.data.updateUser;
      if (result.success) {
        console.log("User updated successfully:", result.message);
        return true;
      } else {
        console.error("Failed to update user:", result.errorMessage);
        throw new Error(result.errorMessage);
      }
    }

    if (response.data?.errors) {
      const errorMessage = response.data.errors[0].message;
      console.error("UpdateUser GraphQL error:", errorMessage);
      throw new Error(errorMessage);
    }

    return false;
  } catch (error: any) {
    console.error("Error updating user:", error);
    throw error;
  }
}

// Removed: get_roles - Roles table no longer exists in database

export async function get_solutions(token: any) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const query = `
    query GetSolutions {
      solutions {
        solutionId
        name
        description
        jiraTicket
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      { query },
      config
    );

    if (response.status === 200 && response.data?.data?.solutions) {
      console.log("GetSolutions response:", response.data.data.solutions);
      return response.data.data.solutions;
    }

    return [];
  } catch (error) {
    console.error("Error fetching solutions:", error);
    return [];
  }
}

export async function get_csol_users(token: any) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const query = `
    query GetCsolUsers {
      csolUsers {
        userId
        email
        isActive
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      { query },
      config
    );

    if (response.status === 200 && response.data?.data?.csolUsers) {
      console.log("GetCsolUsers response:", response.data.data.csolUsers);
      return response.data.data.csolUsers;
    }

    return [];
  } catch (error) {
    console.error("Error fetching CSOL users:", error);
    return [];
  }
}

export async function delete_user(token: any, user_email: string) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const mutation = `
    mutation DeleteUser($email: String!) {
      deleteUser(email: $email) {
        success
        message
        errorMessage
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      {
        query: mutation,
        variables: {
          email: user_email
        }
      },
      config
    );

    if (response.status === 200 && response.data?.data?.deleteUser) {
      const result = response.data.data.deleteUser;
      if (result.success) {
        console.log("User deleted successfully:", result.message);
        return true;
      } else {
        console.error("Failed to delete user:", result.errorMessage);
        return false;
      }
    }

    if (response.data?.errors) {
      console.error("DeleteUser GraphQL error:", response.data.errors[0].message);
      return false;
    }

    return false;
  } catch (error: any) {
    console.error("Error deleting user:", error);
    return false;
  }
}

export async function create_bucket(
  token: any,
  bucketName: string,
  jiraTicket: string,
  awsAccountId: string,
  regionId: string,
  paths: any[], // Array of paths with pathPrefix, displayName, and users
  displayName?: string,
  description?: string,
  managers?: any[], // Array of manager emails
  includeUsernameMetadata?: boolean,
  accessType?: string
  // roleToAssume removed - looked up from DB based on regionId for security
) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const mutation = `
    mutation CreateBucket($input: CreateBucketInput!) {
      createBucket(input: $input) {
        success
        message
        errorMessage
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      {
        query: mutation,
        variables: {
          input: {
            bucketName,
            jiraTicket,
            displayName,
            description,
            regionId,
            awsAccountId,
            includeUsernameMetadata,
            accessType,
            managers: managers || [], // Array of manager emails
            paths: paths || [] // Array of paths
            // roleToAssume not sent - backend looks it up from region's AWS account
          }
        }
      },
      config
    );

    if (response.status === 200 && response.data?.data?.createBucket) {
      const result = response.data.data.createBucket;
      if (result.success) {
        console.log("Bucket created successfully:", result.message);
        return true;
      } else {
        console.error("Failed to create bucket:", result.errorMessage);
        throw new Error(result.errorMessage);
      }
    }

    if (response.data?.errors) {
      const errorMessage = response.data.errors[0].message;
      console.error("CreateBucket GraphQL error:", errorMessage);
      throw new Error(errorMessage);
    }

    return false;
  } catch (error: any) {
    console.error("Error creating bucket:", error);
    throw error;
  }
}

export async function update_bucket(
  token: any,
  bucketData: any
) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const mutation = `
    mutation UpdateBucket($input: UpdateBucketInput!) {
      updateBucket(input: $input) {
        success
        message
        errorMessage
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  // Transform the bucket data to match the GraphQL input structure
  const input = {
    bucketId: bucketData.bucketId,
    bucketName: bucketData.bucketName,
    jiraTicket: bucketData.jiraTicket,
    regionId: bucketData.regionId,
    awsAccountId: bucketData.awsAccountId,
    displayName: bucketData.displayName,
    description: bucketData.description,
    includeUsernameMetadata: bucketData.includeUsernameMetadata,
    accessType: bucketData.accessType,
    // roleToAssume removed - backend looks it up from region's AWS account if regionId changes
    managers: bucketData.managers?.map((manager: any) => ({
      email: manager.email
    })) || [],
    paths: bucketData.paths?.map((path: any) => ({
      pathId: path.pathId,
      pathPrefix: path.pathPrefix,
      displayName: path.displayName,
      description: path.description,
      users: path.users?.map((user: any) => ({
        email: user.email
      })) || []
    })) || []
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      {
        query: mutation,
        variables: { input }
      },
      config
    );

    if (response.status === 200 && response.data?.data?.updateBucket) {
      const result = response.data.data.updateBucket;
      if (result.success) {
        console.log("Bucket updated successfully:", result.message);
        return true;
      } else {
        console.error("Failed to update bucket:", result.errorMessage);
        throw new Error(result.errorMessage);
      }
    }

    if (response.data?.errors) {
      const errorMessage = response.data.errors[0].message;
      console.error("UpdateBucket GraphQL error:", errorMessage);
      throw new Error(errorMessage);
    }

    return false;
  } catch (error: any) {
    console.error("Error updating bucket:", error);
    throw error;
  }
}

/**
 * Get list of bucket names the current user can manage
 */
export async function get_user_managed_buckets(token: any): Promise<string[]> {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const query = `
    query GetUserManagedBuckets {
      userManagedBuckets
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      { query },
      config
    );

    if (response.status === 200 && response.data?.data) {
      const buckets = response.data.data.userManagedBuckets || [];
      console.log("User managed buckets:", buckets);
      return buckets;
    }

    return [];
  } catch (error) {
    console.error("Error getting user managed buckets:", error);
    return [];
  }
}

/**
 * Get user access level as a simple string in a single call (optimized)
 * Returns: "is_admin", "is_power_user", "can_access", or "access_denied"
 */
export async function get_user_access_level_api(token: any): Promise<string> {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  console.log('[get_user_access_level_api] Starting API call', {
    endpoint: graphqlEndpoint,
    hasToken: !!token,
    hasIdToken: !!id_token
  });

  if (!id_token) {
    console.error('[get_user_access_level_api] No id_token found in token object');
    return "access_denied";
  }

  const query = `
    query GetUserAccessLevel {
      getUserAccessLevel
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    console.log('[get_user_access_level_api] Making axios POST request...');
    const response = await axios.post(
      graphqlEndpoint,
      { query },
      config
    );

    console.log('[get_user_access_level_api] Response received:', {
      status: response.status,
      data: response.data,
      errors: response.data?.errors
    });

    if (response.status === 200 && response.data?.data?.getUserAccessLevel) {
      const accessLevel = response.data.data.getUserAccessLevel;
      console.log("[get_user_access_level_api] User access level:", accessLevel);
      return accessLevel;
    }

    console.warn('[get_user_access_level_api] Unexpected response format, returning access_denied');
    return "access_denied";
  } catch (error: any) {
    console.error("[get_user_access_level_api] Error:", {
      message: error?.message,
      response: error?.response?.data,
      errors: error?.response?.data?.errors,
      status: error?.response?.status
    });
    return "access_denied";
  }
}

export async function add_user_to_path(
  token: any,
  pathId: string,
  userEmail: string
) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const mutation = `
    mutation AddUserToPath($pathId: String!, $userEmail: String!) {
      addUserToPath(pathId: $pathId, userEmail: $userEmail) {
        success
        message
        errorMessage
      }
    }
  `;

  const config = {
    headers: {
      "Authorization": `Bearer ${id_token}`,
      "Content-Type": "application/json"
    }
  };

  try {
    const response = await axios.post(
      graphqlEndpoint,
      {
        query: mutation,
        variables: {
          pathId,
          userEmail
        }
      },
      config
    );

    if (response.status === 200 && response.data?.data?.addUserToPath) {
      const result = response.data.data.addUserToPath;
      if (result.success) {
        console.log("User added to path successfully:", result.message);
        return true;
      } else {
        console.error("Failed to add user to path:", result.errorMessage);
        throw new Error(result.errorMessage);
      }
    }

    if (response.data?.errors) {
      const errorMessage = response.data.errors[0].message;
      console.error("AddUserToPath GraphQL error:", errorMessage);
      throw new Error(errorMessage);
    }

    return false;
  } catch (error: any) {
    console.error("Error adding user to path:", error);
    throw error;
  }
}

// Get AdminStore refresh info
export async function get_admin_store_info(token: any) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const query = `
    query GetAdminStoreInfo {
      adminStoreInfo {
        isRefreshed
        lastRefreshTime
        numberOfRefreshes
        adminCount
        powerUserCount
        totalUserCount
      }
    }
  `;

  try {
    const response = await axios.post(
      graphqlEndpoint,
      { query },
      {
        headers: {
          Authorization: `Bearer ${id_token}`,
        },
      }
    );

    if (response.status === 200 && response.data?.data?.adminStoreInfo) {
      return {
        success: true,
        data: response.data.data.adminStoreInfo,
      };
    }

    return { success: false, error: "Failed to get AdminStore info" };
  } catch (error: any) {
    console.error("Error getting AdminStore info:", error);
    return { success: false, error: error.message };
  }
}

// Force refresh AdminStore
export async function refresh_admin_store(token: any) {
  const graphqlEndpoint = "/graphql";
  const id_token = token["id_token"];

  const mutation = `
    mutation RefreshAdminStore {
      refreshAdminStore {
        isRefreshed
        lastRefreshTime
        numberOfRefreshes
        adminCount
        powerUserCount
        totalUserCount
      }
    }
  `;

  try {
    const response = await axios.post(
      graphqlEndpoint,
      { query: mutation },
      {
        headers: {
          Authorization: `Bearer ${id_token}`,
        },
      }
    );

    if (response.status === 200 && response.data?.data?.refreshAdminStore) {
      return {
        success: true,
        data: response.data.data.refreshAdminStore,
      };
    }

    return { success: false, error: "Failed to refresh AdminStore" };
  } catch (error: any) {
    console.error("Error refreshing AdminStore:", error);
    return { success: false, error: error.message };
  }
}
